import { HttpClient } from '@angular/common/http';
import { inject, Injector, Service } from '@angular/core';

import { map, Observable } from 'rxjs';

import { ReviewerDocumentPage } from '@data-access/submissions';
import environment from '@environments/environment';

import { Document } from '@documents/models/document-type';
import type { ReviewerMaterialUpdate } from '../types/reviewer-material.types';

import { DEFAULT_RECURSIVE_FETCH_PARAMS, recursiveFetch } from '@erm/cluster';

@Service({
  autoProvided: false,
})
export class ReviewerMaterialV2Service {
  private readonly _http = inject(HttpClient);
  private readonly _injector = inject(Injector);

  /**
   * Sets a reviewer's completion status for one material.
   *
   * @param documentId - Reviewer material to update.
   * @param isComplete - Completion status to persist.
   * @returns A stream containing the updated reviewer material.
   */
  toggleCompleteReviewMaterial(
    documentId: string,
    isComplete: boolean,
  ): Observable<Document> {
    return this._http.put<Document>(
      `${environment.irisApi}/reviewer-document/${documentId}/`,
      { is_complete: isComplete },
    );
  }

  /**
   * Updates the document type assigned to one reviewer material.
   *
   * @param documentId - Reviewer material to update.
   * @param update - Name and reviewer document type to persist.
   * @returns A stream containing the updated reviewer material.
   */
  updateReviewMaterial(
    documentId: string,
    update: ReviewerMaterialUpdate,
  ): Observable<Document> {
    return this._http.put<Document>(
      `${environment.irisApi}/reviewer-document/${documentId}/`,
      {
        name: update.name,
        reviewer_document_type_id: update.documentTypeId,
      },
    );
  }

  /**
   * Deletes one reviewer material.
   *
   * @param documentId - Reviewer material to remove.
   * @returns A stream that completes when the material is deleted.
   */
  deleteReviewMaterial(documentId: string): Observable<void> {
    return this._http.delete<void>(
      `${environment.irisApi}/reviewer-document/${documentId}/`,
    );
  }

  /**
   * Retrieves every reviewer material for a submission.
   *
   * @param submissionId - Submission whose reviewer materials should be read.
   * @returns A stream containing the flattened paginated response.
   */
  getAllReviewMaterials(
    submissionId: string,
  ): Observable<ReviewerDocumentPage> {
    const _url = `${environment.irisApi}/workspace/${submissionId}/reviewer-document/paginated/`;
    return recursiveFetch<Document>(
      _url,
      this._injector,
      DEFAULT_RECURSIVE_FETCH_PARAMS,
    ).pipe(
      map((r) => {
        const _response: ReviewerDocumentPage = {
          data: r.data?.results ?? [],
          total: r.data?.total ?? 0,
        };
        return _response;
      }),
    );
  }
}
